import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertTriangle, CheckCircle2, Loader2, UserCheck } from "lucide-react";
import apiClient from "@/lib/api";
import toast from "react-hot-toast";

export default function AcceptanceConfirmationModal({
  isOpen,
  onClose,
  applicationId,
  onAccepted,
}) {
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen || !applicationId) return;
    setLoading(true);
    setError(null);
    apiClient
      .get(`/recruitment/applications/${applicationId}/accept-preview`)
      .then((res) => setPreview(res.data))
      .catch((err) => {
        setError(err?.response?.data?.detail || "Échec du chargement de l'aperçu de l'acceptation.");
      })
      .finally(() => setLoading(false));
  }, [isOpen, applicationId]);

  if (!isOpen) return null;

  const handleConfirmAccept = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await apiClient.post(`/recruitment/applications/${applicationId}/accept`, {
        notes: "Accepté définitivement sur la mission client",
      });
      toast.success(
        `Candidat accepté ! ${res.data.closed_applications_count} autre(s) candidature(s) clôturée(s).`
      );
      if (onAccepted) onAccepted(res.data);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.detail || "Échec de l'acceptation du candidat.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && !submitting && onClose()}>
      <DialogContent className="max-w-xl bg-[#101426] text-white border-white/10 rounded-2xl w-[calc(100%_-_2rem)]">
        <DialogTitle className="text-lg font-bold flex items-center gap-2">
          <UserCheck className="h-5 w-5 text-emerald-400" />
          Confirmer l&apos;acceptation du candidat
        </DialogTitle>
        <DialogDescription className="text-xs text-slate-400">
          Cette action valide le recrutement et clôture automatiquement les candidatures concurrentes.
        </DialogDescription>

        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-blue-400" />
          </div>
        ) : error ? (
          <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/20 text-xs text-rose-300">
            {error}
          </div>
        ) : (
          <div className="space-y-4 pt-2">
            <div className="p-3.5 rounded-xl bg-emerald-500/10 border border-emerald-500/20 space-y-1">
              <p className="text-xs font-semibold text-emerald-300">
                Mission validée pour {preview?.candidate_name}
              </p>
              <p className="text-[11px] text-slate-300">
                Besoin client : <strong className="text-white">{preview?.need_title}</strong>
              </p>
            </div>

            {preview?.concurrent_applications?.length > 0 ? (
              <div className="space-y-2">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-400">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  <span>
                    {preview.concurrent_applications.length} autre(s) candidature(s) en cours seront
                    clôturées :
                  </span>
                </div>
                <div className="space-y-1.5 max-h-40 overflow-y-auto pr-1">
                  {preview.concurrent_applications.map((app) => (
                    <div
                      key={app.id}
                      className="p-2.5 rounded-lg bg-white/[0.03] border border-white/10 text-xs flex items-center justify-between"
                    >
                      <div>
                        <p className="font-semibold text-white">{app.need_title}</p>
                        <p className="text-[10px] text-slate-400">Client : {app.company_name}</p>
                      </div>
                      <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-rose-500/10 text-rose-300 border border-rose-500/20">
                        Sera clôturé : Retenu sur une autre mission
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-xs text-slate-400 italic">
                Aucune autre candidature en cours détectée pour ce candidat.
              </p>
            )}

            <div className="p-3 rounded-xl bg-slate-900 border border-white/10 text-[11px] text-slate-300 space-y-1">
              <p className="font-semibold text-white flex items-center gap-1.5">
                <CheckCircle2 className="h-3.5 w-3.5 text-blue-400" />
                Règles appliquées automatiquement :
              </p>
              <ul className="list-disc pl-4 space-y-0.5 text-slate-400">
                <li>Les autres candidatures passent au statut « Retenu sur une autre mission ».</li>
                <li>La disponibilité dans le vivier passe à « En mission / Non disponible ».</li>
                <li>Aucun email automatique n&apos;est envoyé aux clients ou au candidat.</li>
                <li>Vous pourrez convertir ce dossier en mission Pilotage à l&apos;étape suivante.</li>
              </ul>
            </div>

            <div className="flex items-center justify-end gap-3 pt-3 border-t border-white/10">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="px-4 py-2 rounded-xl text-xs font-semibold text-slate-400 hover:text-white"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleConfirmAccept}
                disabled={submitting}
                className="px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-xs font-semibold text-white shadow-md flex items-center gap-2 disabled:opacity-50"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Validation en cours...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    Confirmer l&apos;acceptation
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
